import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiExternalLink, FiMaximize2 } from 'react-icons/fi';

const PreviewContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  background: ${props => props.bg || '#f5f5f5'};
  border-radius: var(--radius-md);
  overflow: hidden;
  cursor: pointer;
  group: preview;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
  transition: transform 0.3s ease;
  
  ${PreviewContainer}:hover & {
    transform: scale(1.05);
  }
`;

const PreviewFrameWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
`;

const PreviewFrame = styled.iframe`
  width: 300%;
  height: 300%;
  border: none;
  background: white;
  transform: scale(0.333);
  transform-origin: 0 0;
  pointer-events: none;
`;

const PreviewOverlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.7) 100%
  );
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 1.5rem;
  opacity: 0;
  transition: opacity 0.3s ease;
  
  ${PreviewContainer}:hover & {
    opacity: 1;
  }
`;

const OverlayText = styled.div`
  color: white;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  font-size: 0.95rem;
`;

const ExpandButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 0.5rem;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
`;

const LoadingState = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    90deg,
    #f0f0f0 0%,
    #f8f8f8 50%,
    #f0f0f0 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;
  
  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

const BrowserChrome = styled.div`
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  padding: 0.5rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const BrowserDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.color};
`;

const BrowserUrl = styled.div`
  flex: 1;
  margin-left: 1rem;
  padding: 0.25rem 0.75rem;
  background: white;
  border-radius: 4px;
  font-size: 0.75rem;
  color: #666;
  font-family: monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProjectPreview = ({ 
  liveUrl, 
  previewUrl, 
  projectName,
  projectIcon, 
  backgroundColor,
  showBrowserChrome = false,
  allowIframe = false 
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleClick = () => {
    if (liveUrl) {
      window.open(liveUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const renderPreview = () => {
    // If we allow iframes and have a preview URL (for demos/safe sites)
    if (allowIframe && previewUrl && !imageError) {
      return (
        <PreviewFrameWrapper>
          <PreviewFrame
            src={previewUrl}
            title={`${projectName} preview`}
            sandbox="allow-scripts allow-same-origin"
            loading="lazy"
          />
        </PreviewFrameWrapper>
      );
    }

    // Use preview image if available and it's an image URL
    if (previewUrl && !allowIframe && !imageError) {
      return (
        <>
          {!imageLoaded && <LoadingState />}
          <PreviewImage
            src={previewUrl}
            alt={`${projectName} preview`}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
            style={{ display: imageLoaded ? 'block' : 'none' }}
            loading="lazy"
          />
        </>
      );
    }

    // Fallback to placeholder with icon or initial
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: backgroundColor || '#f0f0f0',
        color: '#666',
        gap: '1rem'
      }}>
        <div style={{ fontSize: '4rem' }}>
          {projectIcon || projectName?.charAt(0) || '?'}
        </div>
        <div style={{ 
          fontSize: '1.25rem', 
          fontWeight: '600',
          color: '#444',
          textAlign: 'center',
          padding: '0 1rem'
        }}>
          {projectName}
        </div>
      </div>
    );
  };

  return (
    <PreviewContainer 
      bg={backgroundColor} 
      onClick={handleClick}
      role="button"
      tabIndex={0}
      aria-label={`View ${projectName} live site`}
    >
      {showBrowserChrome && (
        <BrowserChrome>
          <BrowserDot color="#FF5F57" />
          <BrowserDot color="#FFBD2E" />
          <BrowserDot color="#28CA42" />
          <BrowserUrl>{liveUrl || 'Preview'}</BrowserUrl>
        </BrowserChrome>
      )}
      
      {renderPreview()}
      
      {liveUrl && (
        <PreviewOverlay
          initial={{ opacity: 0 }}
          whileHover={{ opacity: 1 }}
        >
          <OverlayText>
            <FiExternalLink size={18} />
            View Live Site
          </OverlayText>
          <ExpandButton
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
          >
            <FiMaximize2 size={16} />
          </ExpandButton>
        </PreviewOverlay>
      )}
    </PreviewContainer>
  );
};

export default ProjectPreview;